import { useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { setAccessTokenProvider } from '@/lib/authToken';
import { apiTokenRequest, isAuthConfigured } from '@/auth/config';
import { RequireAuth } from '@/auth/RequireAuth';
import { RequireStaffPermission } from '@/auth/RequireStaffPermission';
import { StaffPermissions } from '@/auth/staffPermissions';
import { StaffLayout } from '@/routes/StaffLayout';
import { Overview } from '@/routes/Overview';
import { Tenants } from '@/routes/Tenants';
import { TenantDetail } from '@/routes/TenantDetail';
import { Diagnostics } from '@/routes/Diagnostics';
import { Audit } from '@/routes/Audit';
import { SignIn } from '@/routes/SignIn';
import { Forbidden } from '@/routes/Forbidden';
import { NotFound } from '@/routes/NotFound';
import { MaintenanceGate } from '@/routes/Maintenance';

export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // No retry, unlike the customer app. A 403 does not become a 200 by
        // asking again, and this console's entire read surface is permission
        // gated — retrying only delays the Forbidden screen someone needs to
        // see in order to understand why.
        retry: false,
        // Nor refetch-on-focus: a support call means alt-tabbing constantly,
        // and re-firing every staff read (each of which is audited server
        // side) on every focus would pad the audit trail with noise.
        refetchOnWindowFocus: false,
      },
    },
  });
}

/**
 * Registers the token provider once, so `apiClient` never has to know about
 * MSAL and tests can stub auth without it — the same seam `web` uses.
 */
function useApiAuth() {
  const { instance } = useMsal();

  useEffect(() => {
    if (!isAuthConfigured) {
      return;
    }

    setAccessTokenProvider(async () => {
      const account = instance.getActiveAccount();
      if (!account) {
        return null;
      }
      try {
        const result = await instance.acquireTokenSilent({ ...apiTokenRequest, account });
        return result.accessToken;
      } catch (error) {
        // Only an interaction-required failure means "send them back to
        // sign-in". Treating every error that way would turn a transient
        // network blip into a surprise redirect that loses their place.
        if (error instanceof InteractionRequiredAuthError) {
          await instance.acquireTokenRedirect(apiTokenRequest);
        }
        return null;
      }
    });
  }, [instance]);
}

/**
 * Routes without the query provider, so tests can mount them with their own
 * client — the same split `web`'s `AppRoutes` uses.
 */
export function AppRoutes() {
  useApiAuth();

  return (
    <Routes>
      <Route path="/signin" element={<SignIn />} />
      <Route
        element={
          <RequireAuth>
            <StaffLayout />
          </RequireAuth>
        }
      >
        <Route index element={<Overview />} />
        {/* M1-33 filled these two in. The guard is the one M1-32 put here —
            the screens changed, the gating did not, which is what having the
            routes exist before their screens was for. */}
        <Route
          path="tenants"
          element={
            <RequireStaffPermission permission={StaffPermissions.readTenants}>
              <Tenants />
            </RequireStaffPermission>
          }
        />
        {/* Guarded separately rather than nested under the list: a tenant page
            is a link pasted into a ticket, so it is entered directly at least
            as often as it is clicked into, and a guard that only covers the
            route someone arrives *through* is not a guard. */}
        <Route
          path="tenants/:tenantId"
          element={
            <RequireStaffPermission permission={StaffPermissions.readTenants}>
              <TenantDetail />
            </RequireStaffPermission>
          }
        />
        {/* M1-35 still has its route and its guard. The screen is a
            placeholder; the permission gating is not, and is asserted in
            staffAuth.test.tsx. */}
        {/* M1-34. The guard is still M1-32's — `diagnostics.account`, the
            weaker of the two, because a Support Technician genuinely needs this
            screen. `diagnostics.global` gates the platform-wide SCOPES inside
            it, and the API enforces that independently. */}
        <Route
          path="diagnostics"
          element={
            <RequireStaffPermission permission={StaffPermissions.diagnosticsAccount}>
              <Diagnostics />
            </RequireStaffPermission>
          }
        />
        {/* M1-35 filled the last slice in. The guard is still M1-32's, and the
            API enforces `staff:audit.read` independently — the screen renders
            the server's own 403 if the two ever disagree. */}
        <Route
          path="audit"
          element={
            <RequireStaffPermission permission={StaffPermissions.readAudit}>
              <Audit />
            </RequireStaffPermission>
          }
        />
        <Route path="forbidden" element={<Forbidden />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

export function App({ queryClient = createQueryClient() }: { queryClient?: QueryClient }) {
  return (
    <QueryClientProvider client={queryClient}>
      {/* Wraps EVERY route, including /signin — an outage during sign-in
          must show maintenance, not a login loop against a dead API (M1-29). */}
      <MaintenanceGate>
        <AppRoutes />
      </MaintenanceGate>
    </QueryClientProvider>
  );
}
